import { Injectable } from '@angular/core';

/**
 * Text Normalizer Service
 * Provides Unicode normalization for Vietnamese text input to handle IME differences.
 * Normalizes text from NFD (Normalization Form Decomposed) or NFC (Normalization Form Composed)
 * to NFC (Canonical Composition) form for consistent validation and storage.
 *
 * Handles issues where Vietnamese IMEs (like UniKey) output characters in different
 * normalization forms, causing inconsistent validation.
 *
 * Example differences:
 * - "đồ dùng nhà bếp" (NFD - decomposed form)
 * - "đồ dùng nhà bếp" (NFC - composed form)
 */
@Injectable({
  providedIn: 'root',
})
export class TextNormalizerService {
  /**
   * Normalizes Vietnamese text input to NFC form.
   * Strips zero-width characters, invisible characters, and normalizes to NFC.
   *
   * @param value - The text value to normalize
   * @param trim - Whether to trim leading/trailing whitespace (default: true)
   * @returns Normalized text in NFC form, or original value if empty/null
   *
   * @example
   * ```typescript
   * // Input: "đồ dùng nhà bếp" (NFD form)
   * // Output: "đồ dùng nhà bếp" (NFC form)
   * const normalized = textNormalizer.normalizeToNFC("đồ dùng nhà bếp");
   * ```
   */
  normalizeToNFC(
    value: string | null | undefined,
    trim: boolean = true
  ): string {
    if (!value) {
      return '';
    }

    let normalized = String(value);

    // Step 1: Remove zero-width and invisible characters that might be inserted by IMEs
    // Zero-width space (U+200B), zero-width non-joiner (U+200C), zero-width joiner (U+200D)
    // Zero-width no-break space (U+FEFF), and other invisible Unicode characters
    // Note: Using replace() with global flag as replaceAll() doesn't support regex
    normalized = normalized
      .replace(/[\u200B-\u200D\uFEFF\u2060\u200E\u200F]/g, '') // Remove zero-width/invisible chars
      .replace(/[\u202A-\u202E]/g, ''); // Remove bidirectional formatting characters

    // Only trim if requested (not during typing to preserve trailing spaces)
    if (trim) {
      normalized = normalized.trim();
    }

    // Step 2: Normalize to NFC (Canonical Composition) form
    // This converts decomposed characters (NFD) to composed characters (NFC)
    // Example: "đồ" (NFD: đ + combining dot below) -> "đồ" (NFC: single composed character)
    normalized = normalized.normalize('NFC');

    // Step 3: Clean up any remaining problematic characters
    // Replace multiple spaces with single space (but preserve single spaces)
    // Note: Using replace() with global flag as replaceAll() doesn't support regex
    normalized = normalized.replace(/\s+/g, ' ');

    return normalized;
  }

  /**
   * Normalizes text and updates the form control value if different.
   * Useful for real-time normalization during input.
   *
   * @param value - The text value to normalize
   * @param control - The Angular form control to update (optional)
   * @returns Normalized text in NFC form
   */
  normalizeAndUpdateControl(
    value: string | null | undefined,
    control?: { setValue: (value: any, options?: any) => void; disabled?: boolean }
  ): string {
    const normalized = this.normalizeToNFC(value);
    const originalValue = String(value || '');

    // Update control value if different and control is provided and not disabled
    if (control && !control.disabled && normalized !== originalValue) {
      // Use setTimeout to avoid infinite validation loops
      setTimeout(() => {
        control.setValue(normalized, { emitEvent: false });
      }, 0);
    }

    return normalized;
  }
}
